import type {ReactElement} from 'react';
import React from 'react';
import type {ColorStorage} from '@Components/color-picker-v2';
import {getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import {PosterEditingBaseColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-color-panel';
import type {ColorFillPropertyType} from '@Components/color-fill';
import {getColorFillTypeFromFillType, getColorObjectArrayFromFillColor, getFillTypeFromColorFillType, getRGBAArrayFromColorStorage} from '@Utils/color.util';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {useAppSelector} from '@/hooks';

export function PageBackgroundColorPanel(): ReactElement<any> | null {
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();

  if (!currentBackgroundObject || !currentPage) {
    throw new Error('current background object or current page object is null!');
  }

  if (currentBackgroundObject.details.type === BackgroundTypeName.IMAGE) {
    return null;
  }

  if (!('fill' in currentBackgroundObject.details)) {
    throw new Error(`fill doesn't exist in currentPageBackground`);
  }

  if (currentBackgroundObject.details.fill.fillType === FillTypes.NONE) {
    return null;
  }

  const getBackgroundColor = (): ColorStorage[] => {
    if (!('fill' in currentBackgroundObject.details)) {
      return [];
    }

    return getColorObjectArrayFromFillColor(currentBackgroundObject.details.fill.fillColor);
  };

  const onChangeColor = (updatedColor: ColorStorage[]): void => {
    void currentPage?.background.updateFromObject(
      {
        details: {
          fill: {
            fillColor: getRGBAArrayFromColorStorage(updatedColor),
          },
        },
      },
      {undoable: false}
    );
  };

  const onChangeEnded = (updatedColor: ColorStorage[]): void => {
    void currentPage?.background.updateFromObject({
      details: {
        fill: {
          fillColor: getRGBAArrayFromColorStorage(updatedColor),
        },
      },
    });
  };

  const onChangeType = (newType: ColorFillPropertyType): void => {
    if (!('fill' in currentPage.background.details)) {
      throw new Error('Page background does not have fill property');
    }

    void currentPage.background.updateFromObject({
      details: {
        fill: {
          fillType: getFillTypeFromColorFillType(newType),
          fillColor: currentPage.background.details.fill.getColorForNewType(getFillTypeFromColorFillType(newType)),
        },
      },
    });
  };

  return (
    <PosterEditingBaseColorPanel
      id="page-background-color-panel"
      type={getColorFillTypeFromFillType(currentBackgroundObject.details.fill.fillType)}
      isGradientEnabled
      color={getBackgroundColor()}
      onChangeColor={onChangeColor}
      onChangeType={onChangeType}
      onChangeEnded={onChangeEnded}
      isAlphaDisabled
    />
  );
}
